
import React, { useState, useRef, useEffect } from 'react';
import { ChatSession, Message, Project, AgentResult, Company } from '../types';

interface ChatWindowProps {
  session: ChatSession;
  isProcessing: boolean;
  isSpeaking?: boolean;
  activeProject?: Project | null;
  activeResults?: AgentResult[];
  dossierTargets?: Company[];
  selectedDossierCompanyId?: string | null;
  onSendMessage: (content: string) => void;
  onStopSpeech?: () => void;
  onToggleVoice: () => void;
  onClone: () => void;
  onOpenMemory: () => void;
  onSelectDossierTarget?: (companyId: string) => void;
  onCopyToDossier?: (message: Message) => void;
  disableCallAgent?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ 
  session, 
  isProcessing, 
  isSpeaking = false,
  activeProject,
  activeResults = [],
  dossierTargets = [],
  selectedDossierCompanyId = null,
  onSendMessage, 
  onStopSpeech,
  onToggleVoice,
  onClone,
  onOpenMemory,
  onSelectDossierTarget,
  onCopyToDossier,
  disableCallAgent,
}) => {
  const [input, setInput] = useState('');
  const [showContextPanel, setShowContextPanel] = useState(true);
  const [isCompactViewport, setIsCompactViewport] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [session.messages, isProcessing]);

  useEffect(() => {
    const updateViewportMode = () => {
      const compact = window.innerWidth < 1320;
      setIsCompactViewport(compact);
      if (compact) {
        setShowContextPanel(false);
      }
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);
    return () => window.removeEventListener('resize', updateViewportMode);
  }, []);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!input.trim() || isProcessing) return;
    onSendMessage(input);
    setInput('');
  };

  const researchCount = activeResults.filter(r => r.agentId === 'researcher').length;
  const leadCount = activeResults.filter(r => r.agentId === 'lead-finder').length;

  return (
    <div className="flex-1 min-w-0 flex flex-col h-full bg-slate-900 border-l border-white/5 overflow-hidden">
      {/* Header */}
      <header className="px-4 pl-16 py-4 md:px-5 md:pl-16 bg-slate-800/60 backdrop-blur-md border-b border-white/5 flex flex-col gap-3 md:flex-row md:justify-between md:items-center shrink-0">
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <button 
            onClick={() => setShowContextPanel(!showContextPanel)}
            className={`w-10 h-10 rounded-2xl transition-all flex items-center justify-center border border-white/5 ${showContextPanel && !isCompactViewport ? 'bg-brand-purple text-white shadow-lg shadow-brand-purple/20' : 'bg-white/5 text-slate-300 hover:text-white'}`}
            title="Toon Project Context"
          >
            <i className={`fas ${showContextPanel ? 'fa-book-open' : 'fa-book'} text-sm`}></i>
          </button>
          <div>
            <h2 className="font-bold text-sm tracking-tight truncate max-w-[140px] md:max-w-[220px] text-white">{session.name}</h2>
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 ${isSpeaking ? 'bg-brand-purple animate-pulse shadow-[0_0_8px_rgba(139,92,246,0.8)]' : 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]'} rounded-full`}></span>
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                {isSpeaking ? 'Aan het vertellen...' : 'Expert Online'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 self-end md:self-auto">
          {isSpeaking && (
            <button 
              onClick={onStopSpeech}
              className="px-5 py-2 bg-red-500 hover:bg-red-600 rounded-2xl font-bold text-[11px] flex items-center gap-2 transition-all shadow-xl shadow-red-500/20 text-white uppercase tracking-widest animate-in slide-in-from-right-1 fade-in duration-300"
            >
              <i className="fas fa-stop"></i>
              <span>Stop</span>
            </button>
          )}
          <button 
            onClick={onClone}
            className="w-10 h-10 rounded-2xl bg-white/5 hover:bg-brand-purple/20 transition-all flex items-center justify-center text-slate-400 hover:text-brand-purple border border-white/5"
            title="Dupliceer Sessie"
          >
            <i className="fas fa-clone text-sm"></i>
          </button>
          {!disableCallAgent && (
            <button 
              onClick={onToggleVoice}
              className="px-5 py-2 bg-brand-purple hover:bg-brand-purple/80 rounded-2xl font-bold text-[11px] flex items-center gap-2 transition-all shadow-xl shadow-brand-purple/20 text-white uppercase tracking-widest"
            >
              <i className="fas fa-waveform"></i>
              <span>Stem</span>
            </button>
          )}
        </div>
      </header>

      <div className="flex-1 min-w-0 flex overflow-hidden">
        {/* Context Panel (Syncing with Project Data) */}
        {showContextPanel && activeProject && !isCompactViewport && (
          <div className="w-[15rem] xl:w-[17rem] shrink-0 bg-slate-800/30 border-r border-white/5 flex flex-col animate-in slide-in-from-left duration-300">
            <div className="p-5 border-b border-white/5 bg-white/5">
              <h3 className="text-[10px] font-black text-brand-purple uppercase tracking-[0.2em] mb-4">Active Grounding</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Project & Doel</label>
                  <div className="bg-[#0a0f1d] border border-white/5 rounded-xl p-3">
                    <p className="text-[11px] font-bold text-white mb-1">{activeProject.name}</p>
                    <p className="text-[10px] text-slate-400 leading-relaxed line-clamp-4">{activeProject.description}</p>
                  </div>
                </div>
                
                <div>
                  <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Knowledge Base ({activeProject.attachments?.length || 0})</label>
                  <div className="space-y-1.5">
                    {activeProject.attachments?.map(a => (
                      <div key={a.id} className="bg-white/5 border border-white/5 rounded-lg px-2 py-1.5 flex items-center gap-2 text-[10px] text-slate-300">
                        <i className="fas fa-file-alt text-brand-purple"></i>
                        <span className="truncate">{a.name}</span>
                      </div>
                    ))}
                    {(!activeProject.attachments || activeProject.attachments.length === 0) && (
                      <p className="text-[10px] text-slate-600 italic px-1">Geen bestanden gekoppeld.</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Research Geheugen</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/5 border border-white/5 rounded-xl p-2 text-center">
                      <div className="text-xs font-bold text-white">{leadCount}</div>
                      <div className="text-[8px] text-slate-500 uppercase">Leads</div>
                    </div>
                    <div className="bg-white/5 border border-white/5 rounded-xl p-2 text-center">
                      <div className="text-xs font-bold text-white">{researchCount}</div>
                      <div className="text-[8px] text-slate-500 uppercase">Dossiers</div>
                    </div>
                  </div>
                </div>

                {dossierTargets.length > 0 && (
                  <div>
                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Doel Dossier</label>
                    <select
                      value={selectedDossierCompanyId || ''}
                      onChange={(e) => onSelectDossierTarget?.(e.target.value)}
                      className="w-full bg-[#0a0f1d] border border-white/10 rounded-xl p-3 text-[11px] text-white outline-none focus:ring-1 focus:ring-brand-purple"
                    >
                      <option value="">Kies dossier voor chat research...</option>
                      {dossierTargets.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={onOpenMemory}
                      className="mt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-brand-purple hover:text-white transition-colors"
                    >
                      Open geselecteerd dossier
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="p-5 flex-1 overflow-y-auto custom-scrollbar">
               <p className="text-[9px] text-slate-500 leading-relaxed text-center opacity-50 uppercase tracking-widest mt-4">
                 Deze context wordt direct gebruikt door de expert agent.
               </p>
            </div>
          </div>
        )}

        {/* Messages */}
        <div 
          ref={scrollRef}
          className="flex-1 min-w-0 overflow-y-auto px-4 pb-5 pt-5 md:px-6 md:pb-6 md:pt-7 md:pr-6 space-y-5 custom-scrollbar bg-[#0a0f1d]"
        >
          {session.messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-6 px-10 animate-in fade-in duration-700">
              <div className="relative">
                <div className="absolute inset-0 bg-brand-purple blur-2xl opacity-20 animate-pulse"></div>
                <div className="relative w-24 h-24 bg-slate-800 rounded-[2.5rem] flex items-center justify-center border border-white/10 shadow-2xl">
                   <i className="fas fa-shield-halved text-4xl text-brand-purple"></i>
                </div>
              </div>
              <div>
                <p className="text-lg font-bold text-white tracking-tight">Klaar voor de start</p>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                  {activeProject 
                    ? `Ik ben geladen met de context van "${activeProject.name}". Hoe kan ik je vandaag technisch ondersteunen?`
                    : "Stel je vraag of start een 1-op-1 stemgesprek met onze salesassistent."
                  }
                </p>
              </div>
            </div>
          )}

          {session.messages.map(msg => (
            <div 
              key={msg.id} 
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2`}
            >
              <div className={`min-w-0 max-w-[92%] md:max-w-[82%] overflow-hidden rounded-[1.5rem] px-4 py-3 md:px-5 md:py-4 shadow-sm relative group ${
                msg.role === 'user' 
                  ? 'bg-brand-purple text-white rounded-tr-none' 
                  : 'bg-slate-800 border border-white/5 text-slate-100 rounded-tl-none'
              }`}>
                <div className="text-sm leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.content}</div>
                {msg.sources && msg.sources.length > 0 && (
                  <div className="mt-4 space-y-3 border-t border-white/10 pt-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Web Bronnen</p>
                      {msg.role === 'model' && selectedDossierCompanyId && onCopyToDossier && (
                        <button
                          onClick={() => onCopyToDossier(msg)}
                          className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-purple hover:text-white transition-colors"
                        >
                          Kopieer naar dossier
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {msg.sources.slice(0, 4).map((source) => (
                        <a
                          key={`${source.title}-${source.uri}`}
                          href={source.uri}
                          target="_blank"
                          rel="noreferrer"
                          className="block rounded-xl border border-white/5 bg-white/5 px-3 py-2 text-xs text-slate-300 hover:border-brand-purple/30 hover:text-white transition-all"
                        >
                          <div className="font-bold">{source.title}</div>
                          <div className="text-[10px] text-slate-500 truncate">{source.uri}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div className={`text-[9px] mt-2 font-bold uppercase tracking-widest opacity-40 transition-opacity group-hover:opacity-100 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {isProcessing && (
            <div className="flex justify-start animate-in fade-in">
              <div className="bg-slate-800/50 border border-white/5 rounded-[1.5rem] rounded-tl-none px-6 py-4">
                <div className="flex gap-1.5">
                  <div className="w-1.5 h-1.5 bg-brand-purple rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-1.5 h-1.5 bg-brand-purple rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-1.5 h-1.5 bg-brand-purple rounded-full animate-bounce"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input area */}
      <footer className="px-3 pb-3 pt-3 md:px-5 md:pb-5 md:pt-4 bg-slate-800/20 border-t border-white/5 shrink-0">
        <form onSubmit={handleSubmit} className="flex min-w-0 gap-2 md:gap-3 items-end max-w-full rounded-[1.5rem] md:rounded-[1.75rem] border border-white/6 bg-slate-900/55 p-2.5 md:p-3">
          <div className="flex-1 min-w-0 relative">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder="Bericht..."
              className="w-full bg-slate-800/80 border border-white/10 rounded-[1.3rem] md:rounded-3xl px-4 py-3 md:px-5 md:py-4 focus:outline-none focus:ring-2 focus:ring-brand-purple/50 transition-all resize-none min-h-[52px] max-h-[160px] text-sm custom-scrollbar text-white placeholder:text-slate-500"
              rows={1}
            />
          </div>
          <button 
            type="submit"
            disabled={!input.trim() || isProcessing}
            className="w-11 h-11 md:w-12 md:h-12 mb-0.5 shrink-0 bg-brand-purple hover:bg-brand-purple/80 disabled:opacity-30 rounded-2xl flex items-center justify-center transition-all shadow-2xl shadow-brand-purple/20 active:scale-90 text-white"
          >
            <i className={`fas ${isProcessing ? 'fa-spinner fa-spin' : 'fa-arrow-up'} text-base`}></i>
          </button>
        </form>
      </footer>
    </div>
  );
};

export default ChatWindow;
